import { useEffect } from 'react';
import type { ServiceDefinition } from '../config/services';
import { siteConfig } from '../config/site';
import { whiteLabelConfig } from '../config/whiteLabel';

type Props = { title: string; description: string; path?: string; noindex?: boolean };
type JsonLdRecord = Record<string, unknown>;

function meta(name: string, content: string, property = false) {
  const attr = property ? 'property' : 'name';
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, name);
    document.head.appendChild(element);
  }
  element.content = content;
}

export function Seo({ title, description, path = '/', noindex = false }: Props) {
  useEffect(() => {
    const fullTitle = title.includes('Malev Studio') ? title : `${title} — Malev Studio`;
    const canonical = new URL(path, siteConfig.siteUrl).toString();
    document.title = fullTitle;
    meta('description', description);
    meta('og:title', fullTitle, true);
    meta('og:description', description, true);
    meta('og:type', 'website', true);
    meta('og:url', canonical, true);
    meta('og:image', `${siteConfig.siteUrl}/images/og/malev-studio-og.png`, true);
    meta('twitter:card', 'summary_large_image');
    meta('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonical;
  }, [title, description, path, noindex]);
  return null;
}

export function JsonLd({ data }: { data: JsonLdRecord | JsonLdRecord[] }) {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

// eslint-disable-next-line react-refresh/only-export-components
export function buildHomeStructuredData(): JsonLdRecord {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteConfig.siteUrl}/#organization`,
        name: siteConfig.name,
        url: siteConfig.siteUrl,
        email: siteConfig.email,
      },
      {
        '@type': 'WebSite',
        '@id': `${siteConfig.siteUrl}/#website`,
        url: siteConfig.siteUrl,
        name: siteConfig.name,
        publisher: { '@id': `${siteConfig.siteUrl}/#organization` },
      },
      {
        '@type': 'ProfessionalService',
        '@id': `${siteConfig.siteUrl}/#web-development-service`,
        name: 'Malev Studio web design and development',
        url: siteConfig.siteUrl,
        provider: { '@id': `${siteConfig.siteUrl}/#organization` },
        areaServed: 'Worldwide',
      },
    ],
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function buildServiceStructuredData(service: ServiceDefinition): JsonLdRecord {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.seo.description,
    url: new URL(`/services/${service.slug}`, siteConfig.siteUrl).toString(),
    provider: { '@id': `${siteConfig.siteUrl}/#organization`, name: siteConfig.name },
    areaServed: 'Worldwide',
    offers: {
      '@type': 'Offer',
      price: service.startingPriceUsd,
      priceCurrency: siteConfig.currency,
    },
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function buildWhiteLabelStructuredData(): JsonLdRecord {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'White-label web development for agencies',
    description: whiteLabelConfig.seo.description,
    url: new URL('/white-label', siteConfig.siteUrl).toString(),
    provider: { '@id': `${siteConfig.siteUrl}/#organization`, name: siteConfig.name },
    areaServed: 'Worldwide',
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Branding, marketing, design and web agencies',
    },
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function buildBreadcrumbStructuredData(items: Array<{ name: string; path: string }>): JsonLdRecord {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: new URL(item.path, siteConfig.siteUrl).toString(),
    })),
  };
}
